import axios from 'axios';
import fnc from './commFunction';

const defaultServerUrl = process.env.NODE_ENV === 'development' ? 'http://localhost:8002' : '';
const serverUrl = (process.env.REACT_APP_SERVER_URL || defaultServerUrl).replace(/\/$/, '');
const PARENT_SESSION_STORAGE_KEY = 'guardianLoginSession';
const PARENT_SESSION_LIST_STORAGE_KEY = 'guardianLoginSessions';
export const GUARDIAN_AUTO_LOGIN_PAUSED_KEY = 'guardianAutoLoginPaused';

export function isGuardianAutoLoginPaused() {
  try { return localStorage.getItem(GUARDIAN_AUTO_LOGIN_PAUSED_KEY) === 'true'; } catch { return false; }
}

export function resumeGuardianAutoLogin() {
  try { localStorage.removeItem(GUARDIAN_AUTO_LOGIN_PAUSED_KEY); } catch { /* 저장소 접근 불가 */ }
}

function pauseGuardianAutoLogin() {
  try { localStorage.setItem(GUARDIAN_AUTO_LOGIN_PAUSED_KEY, 'true'); } catch { /* 저장소 접근 불가 */ }
}

function parseStoredSession(value) {
  if (!value) return null;
  try {
    const session = JSON.parse(value);
    return session && typeof session === 'object' ? session : null;
  } catch (error) {
    return null;
  }
}

function getStoredGuardianSession() {
  for (const storageName of ['sessionStorage', 'localStorage']) {
    try {
      const currentSession = parseStoredSession(window[storageName].getItem(PARENT_SESSION_STORAGE_KEY));
      if (currentSession?.token) return currentSession;
    } catch (error) {
      // 다른 저장소와 암호화된 이전 저장 형식도 아래에서 확인한다.
    }
  }

  const legacySession = parseStoredSession(fnc.getCookie('guardianSession'));
  return legacySession?.token ? legacySession : null;
}

function clearStoredGuardianSession(token, matchingOnly = false) {
  const legacyToken = parseStoredSession(fnc.getCookie('guardianSession'))?.token;
  for (const storageName of ['sessionStorage', 'localStorage']) {
    try {
      const storage = window[storageName];
      const current = parseStoredSession(storage.getItem(PARENT_SESSION_STORAGE_KEY));
      if (!matchingOnly || current?.token === token) {
        storage.removeItem(PARENT_SESSION_STORAGE_KEY);
      }
      if (!matchingOnly || current?.token === token || (!current && legacyToken === token)) {
        storage.removeItem('guardianSession');
      }
      // 인증은 종료하되, 다음 로그인에 사용할 로그인 유지 선택은 보존한다.

      const rawSessions = storage.getItem(PARENT_SESSION_LIST_STORAGE_KEY);
      const sessions = parseStoredSession(rawSessions);
      if (!sessions || Array.isArray(sessions)) continue;

      Object.keys(sessions).forEach((key) => {
        if (String(sessions[key]?.token || '').trim() === token) {
          delete sessions[key];
        }
      });

      if (Object.keys(sessions).length > 0) {
        storage.setItem(PARENT_SESSION_LIST_STORAGE_KEY, JSON.stringify(sessions));
      } else {
        storage.removeItem(PARENT_SESSION_LIST_STORAGE_KEY);
      }
    } catch (error) {
      // 한 저장소를 사용할 수 없어도 나머지 로그인 정보는 제거한다.
    }
  }
}

export function clearStoredAppSession() {
  for (const storageName of ['sessionStorage', 'localStorage']) {
    try {
      window[storageName].removeItem('appSessionToken');
      window[storageName].removeItem('appSessionExpiresAt');
    } catch (error) {
      // 한 저장소를 사용할 수 없어도 나머지 로그인 정보는 제거한다.
    }
  }
  window.dispatchEvent(new Event('billing:updated'));
}

export async function endAppSession() {
  const token = String(fnc.getCookie('appSessionToken') || '').trim();
  const userRole = String(fnc.getCookie('userRole') || '').trim().toLowerCase();
  const guardianSession = userRole === 'parent' ? getStoredGuardianSession() : null;
  const guardianSessionToken = String(guardianSession?.token || '').trim();
  let keepGuardianSession = false;
  try {
    const rememberedSession = parseStoredSession(localStorage.getItem(PARENT_SESSION_STORAGE_KEY));
    keepGuardianSession = userRole === 'parent'
      && localStorage.getItem('guardianRememberDevice') === 'true'
      && Boolean(guardianSessionToken)
      && rememberedSession?.token === guardianSessionToken;
  } catch { /* 저장된 기기 인증을 확인할 수 없으면 일반 로그아웃 */ }
  let preserveCredential = keepGuardianSession;
  if (userRole === 'parent') pauseGuardianAutoLogin();
  const guardianCertCode = String(
    guardianSession?.certCode
    || fnc.getCookie('certCode')
    || guardianSession?.acaCode
    || ''
  ).trim();
  let revoked = !token && !guardianSessionToken;

  try {
    if (token) {
      const requestBody = guardianSessionToken ? { guardianSessionToken } : {};
      if (keepGuardianSession) requestBody.keepGuardianSession = true;
      const response = await axios.post(`${serverUrl}/app/session/logout`, requestBody, {
        headers: { Authorization: `Bearer ${token}` },
        timeout: 8000,
      });
      revoked = response.data?.success === true;
      if (revoked) preserveCredential = keepGuardianSession && response.data?.guardianSessionPreserved === true;
    }
  } catch (error) {
    revoked = false;
  }

  if (userRole === 'parent' && guardianSessionToken && !revoked) {
    try {
      const response = await axios.post(`${serverUrl}/socialLoginRoutes/guardian/logout`, {
        certCode: guardianCertCode,
        token: guardianSessionToken,
        ...(keepGuardianSession ? { keepGuardianSession: true } : {}),
      }, { timeout: 8000 });
      revoked = response.data?.success === true;
      if (revoked) preserveCredential = keepGuardianSession && response.data?.guardianSessionPreserved === true;
    } catch (error) {
      revoked = false;
    }
  }

  if (!revoked && (token || guardianSessionToken)) {
    // 연결 실패 시에도 현재 앱 로그인은 지우고, 저장된 기기 인증은 다음 로그인에서 검증한다.
    console.warn('서버 로그인 세션을 종료하지 못했습니다.');
  }

  clearStoredAppSession();
  if (userRole === 'parent' && !preserveCredential) {
    clearStoredGuardianSession(guardianSessionToken);
  }

  return revoked;
}

// 로그인 화면에서 선택한 학부모의 기기 인증까지 명시적으로 해제한다.
export async function forgetGuardianDevice(session) {
  const token = String(session?.token || '').trim();
  if (!token) return false;
  const isCurrentParent = fnc.getCookie('userRole') === 'parent'
    && getStoredGuardianSession()?.token === token;
  pauseGuardianAutoLogin();
  let revoked = false;
  try {
    const response = await axios.post(`${serverUrl}/socialLoginRoutes/guardian/logout`, {
      certCode: session.certCode || session.acaCode || '',
      token,
    }, { timeout: 8000 });
    revoked = response.data?.success === true;
  } catch { /* 연결되지 않아도 선택한 기기의 저장 인증은 제거한다. */ }
  clearStoredGuardianSession(token, true);
  if (isCurrentParent) clearStoredAppSession();
  return revoked;
}
